import React, { useState, useEffect, ReactNode } from 'react'
import _ from 'lodash'
import { Form } from 'semantic-ui-react'
import { match, P } from 'ts-pattern'
import { FilterSchema } from '@adc/blueprint'
import CheckboxField from '../../../formFields/checkboxField'
import CheckboxButtonField from '../../../formFields/checkboxButtonField'
import StandardFieldLabel from '../../../formFields/formFieldWrapper/standardFieldLabel'
import Chip from '../../chip'
import Text from '../../text'
import { useLocalFilteredOptions } from '../hooks/useLocalFilteredOptions'
import VirtualizedOptionList from './virtualizedOptionList'
import classnames from 'classnames/bind'
import styles from './styles.scss'
const cx = classnames.bind(styles)

/**
 * This bad boy acts a lot like a SingleSelect (taking a options argument),
 * but it's for multiple selections.
 * In the case of web, it's a rendered as a bunch of checkboxes...
 */
type MultiSelectProps = FilterSchema.FilterLayoutProps<
  FilterSchema.FilterMultiSelectDefinition,
  FilterSchema.MultiSelectValueType
>

const toSnakeCase = (
  input:
    | FilterSchema.MultiSelectValueType
    | FilterSchema.MultiSelectControlValueType
) => {
  if (typeof input === 'string') return _.snakeCase(input)
  if (Array.isArray(input)) return _.snakeCase(input.join(' '))
  return 'none'
}

const isSubset = (a: string[], b: string[]) => {
  return _.difference(b, a).length === 0
}

const MultiSelect: React.FunctionComponent<MultiSelectProps> = ({
  compact,
  definition,
  value,
  elmId,
  onChange,
}) => {
  // keep track locally which values are selected
  const [selectedValues, setSelectedValues] = useState(
    _.isNil(value) ? [] : value
  )

  useEffect(() => {
    setSelectedValues(_.isNil(value) ? [] : value)
  }, [value])

  /**
   * This is a little funky, but we need to handle the different types of values
   * that filterState throws at us (for now):
   *
   * value will be like ['a', 'b', 'c']
   * one optionValue (checkbox) will be like 'a' normally,
   * however SRP's filterState will be like ['a'] or ['a', 'b']
   */
  /**
   * Applies exclusive selection logic:
   * - If an exclusive option is selected, deselect all non-exclusive options
   * - If a non-exclusive option is selected, deselect all exclusive options
   */
  const applyExclusive = (
    next: string[] | null,
    toggled: FilterSchema.MultiSelectControlValueType,
    checked: boolean
  ): string[] | null => {
    if (!checked || !next?.length || !definition.options) return next

    const exclusiveValues = new Set(
      definition.options
        .filter(o => o.exclusive)
        .flatMap(o => (Array.isArray(o.value) ? o.value : [o.value]))
        .filter((v): v is string => v !== null)
    )

    if (!exclusiveValues.size) return next

    // Flatten toggled — chip mode passes [option.value] which can be [['not_sure']]
    const added = (
      Array.isArray(toggled) ? toggled : [toggled]
    ).flat() as string[]
    const addedIsExclusive = added.some(v => exclusiveValues.has(v))

    if (addedIsExclusive) {
      // Exclusive picked — keep ONLY the just-clicked exclusive value
      return added.filter(v => exclusiveValues.has(v))
    } else {
      // Normal picked — drop any exclusive values
      return next.filter(v => !exclusiveValues.has(v))
    }
  }

  const onChangeSelection = (
    e:
      | React.ChangeEvent<HTMLInputElement>
      | React.MouseEvent<HTMLDivElement, MouseEvent>,
    definition: FilterSchema.FilterMultiSelectDefinition,
    value: FilterSchema.MultiSelectControlValueType,
    checked: boolean
  ) => {
    let newValues: string[] | null = []
    if (Array.isArray(value)) {
      const currentValues: string[] = selectedValues
      if (checked) {
        newValues = _.uniq<string>([...(currentValues || []), ...value])
      } else {
        newValues = currentValues.filter(v => !value.includes(v))
      }
    } else if (typeof value === 'string') {
      if (checked) {
        newValues = _.uniq<string>([...(selectedValues || []), value])
      } else {
        if (selectedValues?.length) {
          newValues = selectedValues.filter(v => v !== value)
        } else {
          newValues = selectedValues
        }
      }
    }

    // Apply exclusive selection logic if any options are marked exclusive
    newValues = applyExclusive(newValues, value, checked)

    setSelectedValues(newValues || [])
    onChange(definition.name, newValues)
    e?.currentTarget.querySelector('input')?.focus()
  }

  const contains = (
    value: FilterSchema.MultiSelectValueType,
    optionValue: FilterSchema.MultiSelectControlValueType
  ) => {
    return match(definition.controlValueType)
      .with(
        P.when(
          v =>
            v === 'string[]' &&
            Array.isArray(value) &&
            Array.isArray(optionValue)
        ),
        () => isSubset(value as [], optionValue as string[])
      )
      .otherwise(() => {
        if (Array.isArray(value)) return value.includes(optionValue as string)
        return false
      })
  }

  const isChip = definition.mode === 'chip'
  const isCheckboxButton = definition.mode === 'checkboxButton'
  const CheckboxComp = isCheckboxButton ? CheckboxButtonField : CheckboxField

  const layoutDirection = definition.styleIntent?.layoutDirection

  const getRowTitle = (
    option: FilterSchema.MultiSelectOptions[0]
  ): string | undefined => {
    if (typeof option.label !== 'string') return undefined
    if (typeof option.description === 'string')
      return `${option.label} — ${option.description}`
    return option.label
  }

  const renderCheckbox = (
    option: FilterSchema.MultiSelectOptions[0],
    index: number
  ) => (
    <CheckboxComp
      className={cx('no-margin-checkbox')}
      compact={compact}
      elmId={`${elmId}_${toSnakeCase(option.value)}`}
      value={option.value || ''}
      checked={contains(value, option.value)}
      onChange={(
        e: React.ChangeEvent<HTMLInputElement>,
        data: Record<string, unknown>
      ) => {
        onChangeSelection(
          e,
          definition,
          data.value as FilterSchema.MultiSelectControlValueType,
          data.checked as boolean
        )
      }}
      valueAsArray={definition.controlValueType === 'string[]'}
      {...(isCheckboxButton
        ? {
            fluid: true,
            iconName: option.icon,
            label: option.label,
            layoutDirection: 'vertical',
          }
        : {
            helpContent: option.tooltip,
            role: 'info',
            tabIndex: index,
            label: (
              <StandardFieldLabel
                elmId={`${elmId}_${toSnakeCase(option.value)}_label`}
                label={option.label as NonNullable<Exclude<ReactNode, unknown>>}
                subLabel={(option.description as string) || ''}
              />
            ),
          })}
      {...(layoutDirection ? { layoutDirection } : {})}
    />
  )

  // Standard checkbox list mode with localFilterable uses shared VirtualizedOptionList
  if (definition.localFilterable && !isChip && !isCheckboxButton) {
    return (
      <VirtualizedOptionList
        options={definition.options}
        localFilterable={definition.localFilterable}
        localFilterPlaceholder={definition.localFilterPlaceholder}
        localFilterMinLength={definition.localFilterMinLength}
        localFilterMatchMode={definition.localFilterMatchMode}
        initialSelectedValues={value ?? undefined}
        elmId={elmId}
        getRowTitle={getRowTitle}
        containerClassName={cx('multi-select-container')}
        renderItem={(option, index) => renderCheckbox(option, index)}
      />
    )
  }

  // Chip and checkboxButton modes — use local filtering if enabled, but no virtualization
  return (
    <ChipCheckboxButtonLayout
      definition={definition}
      value={value}
      elmId={elmId}
      isChip={isChip}
      isCheckboxButton={isCheckboxButton}
      onChangeSelection={onChangeSelection}
      renderCheckbox={renderCheckbox}
    />
  )
}

/**
 * Chip and checkboxButton modes maintain their own layout (flex wrap / grid).
 * They support localFilterable for search but don't use virtualization.
 */
const ChipCheckboxButtonLayout: React.FC<{
  definition: FilterSchema.FilterMultiSelectDefinition
  value: FilterSchema.MultiSelectValueType
  elmId: string
  isChip: boolean
  isCheckboxButton: boolean
  onChangeSelection: (
    e:
      | React.ChangeEvent<HTMLInputElement>
      | React.MouseEvent<HTMLDivElement, MouseEvent>,
    definition: FilterSchema.FilterMultiSelectDefinition,
    value: FilterSchema.MultiSelectControlValueType,
    checked: boolean
  ) => void
  renderCheckbox: (
    option: FilterSchema.MultiSelectOptions[0],
    index: number
  ) => React.ReactNode
}> = ({
  definition,
  value,
  elmId,
  isChip,
  isCheckboxButton,
  onChangeSelection,
  renderCheckbox,
}) => {
  const { filterText, setFilterText, filteredOptions, hasNoResults } =
    useLocalFilteredOptions({
      options: definition.options,
      enabled: definition.localFilterable,
      minLength: definition.localFilterMinLength,
      matchMode: definition.localFilterMatchMode,
    })

  return (
    <div className={cx({ 'local-filterable': definition.localFilterable })}>
      {definition.localFilterable && (
        <div className={cx('local-filter-input')}>
          <input
            type='text'
            placeholder={
              definition.localFilterPlaceholder || 'Filter options...'
            }
            value={filterText}
            onChange={e => setFilterText(e.target.value)}
            data-elm-id={`${elmId}_local_filter`}
          />
        </div>
      )}
      {hasNoResults ? (
        <Text color='gray' size='small'>
          No matching options
        </Text>
      ) : (
        <Form
          className={cx('multi-select-container', {
            'checkbox-button': isCheckboxButton,
            'chip-container': isChip,
            'two-columns': !!definition.styleIntent?.twoColumns,
            'left-justified': isChip,
          })}
        >
          {filteredOptions.map((option, index) =>
            isChip ? (
              <Chip
                content={option.label}
                elmId={`${elmId}_${definition.name}`}
                iconName={option.icon}
                key={String(option.value)}
                size={definition.styleIntent?.chipSize}
                onClick={(e: React.MouseEvent<HTMLDivElement>) =>
                  onChangeSelection(
                    e,
                    definition,
                    [option.value] as FilterSchema.MultiSelectControlValueType,
                    !value?.find(v => v === option.value)
                  )
                }
                selectionStyle='light'
                type='filter'
                isSelected={!!value?.find(v => v === option.value)}
              />
            ) : (
              <React.Fragment key={String(option.value)}>
                {renderCheckbox(option, index)}
              </React.Fragment>
            )
          )}
        </Form>
      )}
    </div>
  )
}

export default MultiSelect
